/** Checks the published worker before selecting the waiting worker for an explicit update. */
export function waitForWaitingWorker(
  registration: ServiceWorkerRegistration,
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<boolean> {
  signal?.throwIfAborted();

  return new Promise<boolean>((resolve, reject) => {
    let settled = false;
    let checked = false;
    const cleanupFns: Array<() => void> = [];
    const watched = new Set<ServiceWorker>();

    function finish(found: boolean, error?: unknown): void {
      if (settled) return;
      settled = true;
      cleanupFns.forEach((fn) => fn());
      if (error !== undefined) reject(error);
      else resolve(found);
    }

    function inspect(): void {
      const installing = registration.installing;
      if (installing && !watched.has(installing)) {
        watched.add(installing);
        const onStateChange = (): void => {
          if (installing.state === 'redundant') {
            finish(false, new Error('SERVICE_WORKER_INSTALL_FAILED'));
          } else {
            inspect();
          }
        };
        installing.addEventListener('statechange', onStateChange);
        cleanupFns.push(() => installing.removeEventListener('statechange', onStateChange));
      }
      // A previously waiting worker can belong to an older release. Only use
      // waiting after update() confirms the published script and installation ends.
      if (!checked) return;
      if (installing && installing !== registration.waiting) return;
      finish(Boolean(registration.waiting));
    }

    const onUpdateFound = (): void => inspect();
    registration.addEventListener('updatefound', onUpdateFound);
    cleanupFns.push(() => registration.removeEventListener('updatefound', onUpdateFound));

    const timer = setTimeout(
      () => finish(false, new Error('SERVICE_WORKER_INSTALL_TIMEOUT')),
      timeoutMs,
    );
    cleanupFns.push(() => clearTimeout(timer));
    const onAbort = () =>
      finish(false, signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    signal?.addEventListener('abort', onAbort, { once: true });
    cleanupFns.push(() => signal?.removeEventListener('abort', onAbort));

    inspect();
    try {
      void registration.update().then(
        () => {
          checked = true;
          inspect();
        },
        (error: unknown) => finish(false, error),
      );
    } catch (error) {
      finish(false, error);
    }
  });
}
